import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import type {
  BulkProductsRequest,
  BulkResultItem,
  StaffProfileStoresItem,
} from '@nucleus-commerce/api-client/admin';

const text = (value: unknown) => (typeof value === 'string' && value !== '' ? value : null);

export interface BulkResultDetailProps {
  item: BulkResultItem;
  /** The bulk action the result answers; some codes mean something else per action. */
  action: BulkProductsRequest['action'];
  /** The session's stores, to name `meta.storeId`. */
  stores: ReadonlyArray<Pick<StaffProfileStoresItem, 'id' | 'name'>>;
  /** Called when a link leaves the result dialog. */
  onNavigate: () => void;
}

/**
 * What one refused row of a products bulk call means, under its generic reason: the owning
 * rotation's link for SCHEDULE_CONFLICT, and for `schedule` the master status, the store whose
 * dates would be out of order, or the store where the handle is taken, from the item's `meta`.
 */
export function BulkResultDetail({ item, action, stores, onNavigate }: BulkResultDetailProps) {
  const { t } = useTranslation(['products', 'common']);
  const meta = item.meta ?? {};
  const storeId = text(meta.storeId);
  const store = storeId
    ? (stores.find((s) => s.id === storeId)?.name ?? t('products:bulk.result.unknownStore'))
    : null;

  const scheduleId = item.code === 'SCHEDULE_CONFLICT' ? text(meta.scheduleId) : null;
  if (scheduleId) {
    return (
      <Link
        to="/org/schedules/$scheduleId"
        params={{ scheduleId }}
        className="text-xs underline"
        onClick={onNavigate}
      >
        {t('products:bulk.result.openRotation')}
      </Link>
    );
  }
  if (action !== 'schedule') return null;

  const productStatus = text(meta.productStatus);
  const line =
    item.code === 'CONFLICT' && productStatus
      ? t('products:bulk.result.notActive', {
          status: t(`common:status.${productStatus}`, { defaultValue: productStatus }),
        })
      : item.code === 'INVALID_INPUT' && store
        ? t('products:bulk.result.datesOutOfOrder', { store })
        : item.code === 'ALREADY_EXISTS' && store && text(meta.handle)
          ? t('products:bulk.result.handleTaken', { handle: text(meta.handle), store })
          : null;
  return line ? <span className="text-xs">{line}</span> : null;
}
